import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { vehicleAPI } from '../api';

export default function VehicleSelector() {
  const navigate = useNavigate();
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { vehicleAPI.getYears().then(r => setYears(r.data)).catch(() => {}); }, []);

  const onYearChange = useCallback(async (year) => {
    setSelectedYear(year); setSelectedMake(''); setSelectedModel(''); setMakes([]); setModels([]);
    if (year) { setLoading(true); try { const r = await vehicleAPI.getMakes(year); setMakes(r.data); } catch {} finally { setLoading(false); } }
  }, []);

  const onMakeChange = useCallback(async (make) => {
    setSelectedMake(make); setSelectedModel(''); setModels([]);
    if (make && selectedYear) { setLoading(true); try { const r = await vehicleAPI.getModels(selectedYear, make); setModels(r.data); } catch {} finally { setLoading(false); } }
  }, [selectedYear]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedYear) params.set('year', selectedYear);
    if (selectedMake) params.set('make', selectedMake);
    if (selectedModel) params.set('model', selectedModel);
    navigate(`/products?${params.toString()}`);
  };

  const sc = "w-full bg-white border-2 border-gray-200 rounded px-4 py-3 text-sm text-gray-900 font-medium focus:border-[#0F766E] focus:ring-2 focus:ring-[#0F766E]/20 focus:outline-none transition-all appearance-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed";

  return (
    <div className="bg-white p-6 sm:p-8 rounded-lg shadow-2xl border-t-4 border-[#F97316] w-full max-w-4xl mx-auto" data-testid="vehicle-selector">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-1.5 h-8 bg-[#F97316] rounded-full" />
        <div>
          <h3 className="text-lg font-black text-gray-900 tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>Find Parts for Your Vehicle</h3>
          <p className="text-xs text-gray-500 mt-0.5">Select your vehicle to see compatible parts</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Year</label>
          <select value={selectedYear} onChange={e => onYearChange(e.target.value)} className={sc} data-testid="vehicle-year-select"><option value="">Select Year</option>{years.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
        <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Make</label>
          <select value={selectedMake} onChange={e => onMakeChange(e.target.value)} disabled={!selectedYear} className={sc} data-testid="vehicle-make-select"><option value="">Select Make</option>{makes.map(m => <option key={m} value={m}>{m}</option>)}</select></div>
        <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Model</label>
          <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)} disabled={!selectedMake} className={sc} data-testid="vehicle-model-select"><option value="">Select Model</option>{models.map(m => <option key={m} value={m}>{m}</option>)}</select></div>
        <div className="flex items-end">
          <button onClick={handleSearch} disabled={!selectedYear || loading} className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-3 px-4 rounded transition-all active:scale-95 disabled:opacity-40 flex items-center justify-center gap-2" data-testid="vehicle-search-btn">Search Parts <ChevronRight size={18} /></button>
        </div>
      </div>
    </div>
  );
}
